import React, { useState, useEffect } from 'react';
import { SOCIAL_LINKS } from '../constants';

const FOOTER_GRADIENT_FADE_THRESHOLD_PX = 300;

interface FooterProps {
  theme: 'light' | 'dark';
  onThemeToggle: () => void;
  bgmMuted: boolean;
  onBgmToggle: () => void;
  /** 검색 결과가 나온 검색 화면일 때만 스크롤에 따른 그라데이션/텍스트 페이드 적용 */
  enableScrollFade?: boolean;
}

export default function Footer({ theme, onThemeToggle, bgmMuted, onBgmToggle, enableScrollFade = false }: FooterProps) {
  const [gradientFaded, setGradientFaded] = useState(false);

  useEffect(() => {
    if (!enableScrollFade) {
      setGradientFaded(false);
      return;
    }
    const checkDistanceFromBottom = () => {
      const distanceFromBottom =
        document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
      setGradientFaded(distanceFromBottom < FOOTER_GRADIENT_FADE_THRESHOLD_PX);
    };
    checkDistanceFromBottom();
    window.addEventListener('scroll', checkDistanceFromBottom, { passive: true });
    return () => window.removeEventListener('scroll', checkDistanceFromBottom);
  }, [enableScrollFade]);

  return (
    <footer className={`social-footer ${gradientFaded ? 'social-footer--gradient-faded' : ''}`}>
      <div className="footer-left">
        <p className="footer-copyright">© 2026 MONEYBALL. All rights reserved.</p>
      </div>
      <div className="footer-right">
        <button
          type="button"
          onClick={onBgmToggle}
          className="social-link bgm-toggle"
          aria-label={bgmMuted ? 'Unmute BGM' : 'Mute BGM'}
        >
          {bgmMuted ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.02c2.89.86 5 3.54 5 6.76zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          )}
        </button>
        <a
          href={SOCIAL_LINKS.PUMP_FUN}
          target="_blank"
          rel="noopener noreferrer"
          className="social-link pump-fun-link"
          aria-label="Visit our pump.fun chart"
        >
          <img
            src="/static/pump_fun_logo"
            alt="pump.fun"
            width="24"
            height="24"
          />
        </a>
        <a
          href={SOCIAL_LINKS.TWITTER}
          target="_blank"
          rel="noopener noreferrer"
          className="social-link"
          aria-label="Follow us on X (Twitter)"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>
        <button
          onClick={onThemeToggle}
          className="social-link theme-toggle"
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? (
            // Moon icon for dark mode
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"/>
            </svg>
          ) : (
            // Sun icon for light mode
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"/>
            </svg>
          )}
        </button>
      </div>
    </footer>
  );
}
